import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Button } from 'components/ui/Button';
import { Checkbox } from 'components/ui/Checkbox';
import { Input } from 'components/ui/Input';
import { SectionLabel } from 'components/ui/SectionLabel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/Select';
import { Toast } from 'components/ui/Toast';
import { BackendRoute, heardFromToLabelMap, inquiryTypeToLabelMap, RouterKey } from 'consts';
import { useLanguage } from 'context/language.context';
import { StringKey } from 'dictionaries/string-key.enum';
import { cn } from 'lib/utils';
import { motion } from 'motion/react';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import apiService from 'services/api.service';
import { HttpException } from 'services/http.exception';
import { HeardFrom, InquiryType } from 'types';
import { contactSchema } from 'validations';
import z from 'zod';

export const ContactUsSection = () => {
  const { t, direction } = useLanguage();
  const navigate = useNavigate();

  const form = useForm<z.infer<typeof contactSchema>>({
    resolver: zodResolver(contactSchema),
  });

  const contactUs = useCallback(
    async (data: z.infer<typeof contactSchema>) => {
      await apiService.post(
        {
          endpoint: BackendRoute.CONTACT_US,
        },
        {
          body: {
            ...data,
            inquiryType: t(inquiryTypeToLabelMap[data.inquiryType]),
            heardFrom: t(heardFromToLabelMap[data.heardFrom]),
          },
        },
      );
    },
    [t],
  );

  const contactUsMutation = useMutation({
    mutationFn: contactUs,
    onSuccess: () => {
      form.reset();
      toast(
        <Toast title={t(StringKey.MESSAGE_SENT)} description={t(StringKey.MESSAGE_SENT_DESC)} />,
        {
          className: 'p-0 w-[calc(100%-32px)] mx-4 xs:m-0 xs:w-[400px] bg-transparent',
        },
      );
    },
    onError: (error) => {
      toast(
        <Toast
          type="error"
          title={t(StringKey.TOAST_ERROR_TITLE)}
          description={
            error instanceof HttpException
              ? error.body?.statusCode === 429
                ? t(StringKey.TOO_MANY_REQUESTS_MSG)
                : error.body?.message
              : t(StringKey.TOAST_ERROR_DESC)
          }
        />,
        {
          className: 'p-0 w-[calc(100%-32px)] mx-4 xs:m-0 xs:w-[400px] bg-transparent',
        },
      );
    },
  });

  const onSubmit = (data: z.infer<typeof contactSchema>) => {
    contactUsMutation.mutate({ ...data });
  };

  const {
    firstName: firstNameError,
    lastName: lastNameError,
    email: emailError,
    inquiryType: inquiryTypeError,
    heardFrom: heardFromError,
    message: messageError,
    agreed: agreedError,
  } = form.formState.errors;

  return (
    <section
      id="contactus"
      className="relative w-full overflow-x-hidden py-[80px] md:py-[150px] px-20 max-md:px-4"
    >
      <div className="z-0 absolute w-full bottom-0 left-0 select-none">
        <img
          className="w-full hidden md:block"
          src="/assets/contact-us-bg.png"
          alt="Contact us background"
        />
        <img
          className="w-full block md:hidden"
          src="/assets/contact-us-bg-mobile.png"
          alt="Contact us background"
        />
      </div>
      <div className="relative z-2 px-4 flex flex-col gap-16 max-md:gap-12 max-md:px-0">
        <div className="relative lg:max-w-[80%]">
          <SectionLabel>{t(StringKey.CONTACT_US_LABEL)}</SectionLabel>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ ease: 'easeOut', duration: 0.6, delay: 0 }}
          >
            <h3 className="bg-clip-text bg-label-gradient mt-2 font-semibold text-[30px] leading-[38px] md:text-4xl text-transparent">
              {t(StringKey.CONTACT_US_HEADING)}
            </h3>
            <p className="mt-3 lg:max-w-[80%]">{t(StringKey.CONTACT_US_SUBTITLE)}</p>
          </motion.div>
        </div>
        <motion.form
          initial={{ opacity: 0, scale: 0.5 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ ease: 'easeOut', duration: 0.6, delay: 0.2 }}
          className="border-[1px] border-[#333333] bg-[linear-gradient(144.42deg,#151515_0%,#101010_100%)] rounded-3xl p-5 md:p-8 lg:p-10"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-8 max-md:gap-4">
              <div className="flex justify-between gap-6 max-md:flex-col max-md:gap-4">
                <Input
                  label={t(StringKey.FIRST_NAME)}
                  placeholder={t(StringKey.ENTER_FIRST_NAME)}
                  {...form.register('firstName')}
                  errorMessage={firstNameError?.message}
                />
                <Input
                  label={t(StringKey.LAST_NAME)}
                  placeholder={t(StringKey.ENTER_LAST_NAME)}
                  {...form.register('lastName')}
                  errorMessage={lastNameError?.message}
                />
              </div>
              <div className="flex flex-wrap justify-between gap-6 max-md:gap-4">
                <Input
                  dir="ltr"
                  label={t(StringKey.EMAIL)}
                  placeholder={t(StringKey.ENTER_EMAIL)}
                  {...form.register('email')}
                  errorMessage={emailError?.message}
                  className={cn('min-w-56', direction === 'rtl' ? 'text-right' : 'text-left')}
                />
                <Select
                  label={t(StringKey.INQUIRY_TYPE)}
                  errorMessage={inquiryTypeError?.message}
                  onValueChange={(value) => {
                    form.setValue('inquiryType', value as InquiryType);
                    form.trigger('inquiryType');
                  }}
                  value={t(inquiryTypeToLabelMap[form.watch('inquiryType') as InquiryType]) || null}
                  className="flex-1 min-w-56"
                >
                  <SelectTrigger error={!!inquiryTypeError?.message}>
                    <SelectValue placeholder={t(StringKey.SELECT_INQUIRY_TYPE)} />
                  </SelectTrigger>
                  <SelectContent className="max-h-48">
                    {Object.values(InquiryType).map((value, index) => (
                      <SelectItem key={index} value={value}>
                        {t(inquiryTypeToLabelMap[value])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  label={t(StringKey.HEARD_FROM)}
                  errorMessage={heardFromError?.message}
                  onValueChange={(value) => {
                    form.setValue('heardFrom', value as HeardFrom);
                    form.trigger('heardFrom');
                  }}
                  value={t(heardFromToLabelMap[form.watch('heardFrom') as HeardFrom]) || null}
                  className="flex-1 min-w-56"
                >
                  <SelectTrigger error={!!heardFromError?.message}>
                    <SelectValue placeholder={t(StringKey.SELECT)} />
                  </SelectTrigger>
                  <SelectContent className="max-h-48">
                    {Object.values(HeardFrom).map((value, index) => (
                      <SelectItem key={index} value={value}>
                        {t(heardFromToLabelMap[value])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input
                label={t(StringKey.MESSAGE)}
                placeholder={t(StringKey.WRITE_YOUR_MESSAGE)}
                {...form.register('message')}
                errorMessage={messageError?.message}
              />
            </div>

            <div className="flex items-center justify-between max-md:gap-4 max-md:flex-col max-md:items-stretch">
              <div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={!!form.watch('agreed')}
                    onCheckedChange={(checked: boolean) => {
                      form.setValue('agreed', checked);
                      if (!form.formState.isSubmitted) form.trigger('agreed');
                    }}
                  />
                  <div className="text-[#969696] text-sm lg:text-m">
                    {t(StringKey.AGREE_TERMS_POLICY)}{' '}
                    <span
                      className="cursor-pointer underline"
                      onClick={() => navigate({ to: RouterKey.TERMS_OF_USE })}
                    >
                      {t(StringKey.TERMS_OF_USE)}
                    </span>
                    {' ' + t(StringKey.AND) + ' '}
                    <span
                      className="cursor-pointer underline"
                      onClick={() => navigate({ to: RouterKey.PRIVACY_POLICY })}
                    >
                      {t(StringKey.PRIVACY_POLICY)}
                    </span>
                  </div>
                </div>
                {agreedError?.message && (
                  <p className="w-0 h-4 md:h-0 min-w-[calc(100%-32px)] text-error text-sm font-light mt-1 mx-4">
                    {t(agreedError.message as StringKey) ?? agreedError.message}
                  </p>
                )}
              </div>
              <Button type="submit">{t(StringKey.SUBMIT_FORM)}</Button>
            </div>
          </div>
        </motion.form>
      </div>
    </section>
  );
};
